import { useMemo } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useSlideCheckout } from '@zenbusiness/application-commons-ecommerce';
import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import { usePriceList } from '@zenbusiness/application-commons-pricelist';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import Tooltip from '@zenbusiness/zui/Tooltip';

import { useAccountSettings } from '#client/hooks/useAccountSettings';
import { useAdpReferralSubmit } from '#client/hooks/useAdpReferralSubmit';
import usePageCheckoutComplete from '#client/hooks/usePageCheckoutComplete';
import useProductCtaUrl from '#client/hooks/useProductCtaUrl';
import { useProductLandingContext } from '#client/pages/ProductLanding/contexts/useProductLanding';
import { useJurisdictions } from '#client/pages/ProductLanding/hooks/useJurisdictions';
import { useProductLandingClickstreamProps } from '#client/pages/ProductLanding/hooks/useProductLandingClickstreamProps';

import * as Styled from './BuyBox.style';
import { useBuyBoxContext } from './BuyBoxContext';

const CTAButton = () => {
  const { isFederated } = useProductLandingContext();
  const {
    ctaIcon,
    ctaConfigContext,
    ctaProps,
    ctaType,
    interval,
    buildCtaUrl,
    onCtaActionComplete,
    onCtaActionFailure,
    onCtaActionSuccess,
    openInNewTab,
    referralComments,
    serviceCode,
    serviceToAddToCart,
  } = useBuyBoxContext();

  const { ctaDisabledTooltip, isBuyEnabled = true, ctaText: label, allowsReviewPurchased = false } = ctaProps;

  const { palette } = useTheme();
  const buildProductCtaUrl = useProductCtaUrl();
  const { get } = usePriceList();
  const { formatMessage } = useIntl();
  const { openCheckout, closeCheckout } = useSlideCheckout();
  const { handleCheckoutComplete, getCheckoutCompleteUrl } = usePageCheckoutComplete();
  const { submitAdpReferral, loading: adpReferralLoading } = useAdpReferralSubmit();
  const { data: accountInfo } = useAccountSettings();
  const { businessEntity } = useBusinessEntity();
  const checkoutCompleteUrl = getCheckoutCompleteUrl(serviceCode);
  const { jurisdictions, loading: jurisdictionsLoading, error: jurisdictionsError } = useJurisdictions(serviceCode);

  const servicesToAddToCart = useMemo(() => {
    if (!serviceToAddToCart) {
      return [];
    }
    return [
      {
        ...serviceToAddToCart,
        jurisdictions,
      },
    ];
  }, [serviceToAddToCart, jurisdictions]);

  const isInternalCheckout = ctaType === 'internalCheckout';
  const isAdpReferral = ctaType === 'adpReferral';
  const isJurisdictionsDisabled = jurisdictionsLoading || !!jurisdictionsError;
  const isAdpReferralSubmitted = Boolean(ctaConfigContext?.adpReferralSubmitted);
  const personalizationClickstreamProps = useProductLandingClickstreamProps();

  const { clickEventProps, ctaUrl } = useMemo(() => {
    if (!serviceCode) {
      return {
        clickEventProps: {
          ...personalizationClickstreamProps,
          elementName: 'product_link_pressed',
          elementText: 'cta button',
        },
        ctaUrl: '',
      };
    }

    if (isInternalCheckout) {
      return {
        clickEventProps: {
          ...personalizationClickstreamProps,
          elementName: 'product_link_pressed',
          elementText: 'cta button',
          elementValue: serviceCode,
        },
        ctaUrl: '',
      };
    }

    if (isAdpReferral) {
      return {
        clickEventProps: {
          ...personalizationClickstreamProps,
          elementName: 'product_link_pressed',
          elementText: 'cta button',
          elementValue: serviceCode,
        },
        ctaUrl: '',
      };
    }

    const url = buildProductCtaUrl(serviceCode);
    const resolvedCtaUrl = typeof buildCtaUrl === 'function' ? buildCtaUrl(url, { interval, serviceCode }) : url;

    const clickProps = {
      ...personalizationClickstreamProps,
      destination: resolvedCtaUrl,
      elementName: 'product_link_pressed',
      elementText: 'cta button',
      elementValue: serviceCode,
    };

    return {
      clickEventProps: clickProps,
      ctaUrl: resolvedCtaUrl,
    };
  }, [
    serviceCode,
    isInternalCheckout,
    isAdpReferral,
    buildProductCtaUrl,
    buildCtaUrl,
    interval,
    personalizationClickstreamProps,
  ]);

  const getCheckoutParams = () => {
    // Always use servicesToAddToCart when available
    if (servicesToAddToCart.length > 0) {
      return { servicesToAddToCart };
    }

    const checkoutParams = { jurisdictions };

    // Interval can be not specified, in that case we use it as a service name
    if (!interval) {
      return { ...checkoutParams, serviceNames: [serviceCode] };
    }

    // If interval is specified, we use it to get the service pricing uuids
    // to avoid plans duplication that have same SKU for monthly and annual plans
    const priceListItems = get([serviceCode], {
      interval,
    });

    if (!priceListItems || priceListItems.length === 0) {
      return { ...checkoutParams, serviceNames: [serviceCode] };
    }

    const cartServices = {
      servicePricingUuids: priceListItems
        .map(({ pricing }) => pricing.servicePricingUuid)
        .flat()
        .filter(Boolean),
    };

    return { ...checkoutParams, ...cartServices };
  };

  const onClickWithNavigate = () => {
    if (isInternalCheckout && !allowsReviewPurchased && !isJurisdictionsDisabled) {
      const checkoutParams = getCheckoutParams();

      openCheckout({
        ...checkoutParams,
        onClose: ({ isPurchaseComplete, orderUuid }) => {
          closeCheckout();
          if (isPurchaseComplete) {
            handleCheckoutComplete(serviceCode, orderUuid);
          }
        },
      });
    }
  };

  const handleAdpReferralClick = async () => {
    if (isAdpReferralSubmitted) {
      return;
    }

    try {
      const wasSubmitted = await submitAdpReferral({
        firstName: accountInfo?.firstName || '',
        lastName: accountInfo?.lastName || '',
        phone: accountInfo?.accountPhone || '',
        email: accountInfo?.accountEmail || '',
        company: businessEntity?.name || '',
        // Identifies which ADP product the lead came from — ADP routes on this text.
        ...(referralComments && { comments: referralComments }),
      });

      if (wasSubmitted) {
        onCtaActionSuccess?.({ ctaType, serviceCode, result: 'submitted' });
      } else {
        onCtaActionFailure?.({ ctaType, serviceCode, error: null });
      }
    } catch (error) {
      onCtaActionFailure?.({ ctaType, serviceCode, error });
    } finally {
      onCtaActionComplete?.({ ctaType, serviceCode });
    }
  };

  if (!serviceCode) {
    return (
      <Button>
        <FormattedMessage id={label} />
      </Button>
    );
  }

  // Tooltip in separate element since disabled buttons don't trigger hover events
  if ((!isBuyEnabled && ctaDisabledTooltip) || jurisdictionsError) {
    return (
      <Box position="relative" width="100%">
        <Button disabled fullWidth>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FormattedMessage id={label} />
            <Tooltip
              arrow={false}
              componentsProps={Styled.TOOLTIP_SLOT_PROPS}
              enterTouchDelay={0}
              leaveTouchDelay={15000}
              title={
                <Text size="xs" weight="regular">
                  <FormattedMessage id={ctaDisabledTooltip} />
                </Text>
              }
              placement="top-start"
            >
              <Styled.TooltipIconWrapper>
                <SharedAsset alt="" color={palette.info.light} src="/s2/material-icons/info.svg" width={20} />
              </Styled.TooltipIconWrapper>
            </Tooltip>
          </Stack>
        </Button>
      </Box>
    );
  }

  return (
    <Button
      clickstreamProps={clickEventProps}
      disabled={!isBuyEnabled}
      fullWidth
      {...(!isInternalCheckout && !isAdpReferral && { href: ctaUrl })}
      {...(allowsReviewPurchased && { href: checkoutCompleteUrl })}
      {...(isInternalCheckout && { onClick: onClickWithNavigate })}
      {...(isAdpReferral && { onClick: handleAdpReferralClick })}
      {...((openInNewTab || (isFederated && !isInternalCheckout)) && !isAdpReferral && { target: '_blank' })}
      {...(ctaIcon &&
        ctaIcon.src && {
          icon: (
            <SharedAsset
              alt={ctaIcon.alt ? formatMessage({ id: ctaIcon.alt }) : ''}
              src={ctaIcon.src}
              color={palette.text.white}
              width={28}
            />
          ),
          iconPosition: 'end',
        })}
      loading={jurisdictionsLoading || adpReferralLoading}
    >
      <FormattedMessage id={label} />
    </Button>
  );
};

export default CTAButton;
